import { useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { NewbornProgram } from '@/features/programs/model/types';
import { fetchNewbornProgramQueryAtom } from '../model/fetchNewbornProgramQueryAtom';
import { isBabyBornMutationAtom } from '../model/isBabyBornMutationAtom';

const haveYouGivenBirthLastShownKey = 'haveYouGivenBirthModalLastShownUtcMs';

export const useNewbornProgram = (initialNewbornProgram: NewbornProgram) => {
    const [showEditDueDateModal, setShowEditDueDateModal] =
        useState<boolean>(false);
    const [showHaveYouGivenBirthModal, setShowHaveYouGivenBirthModal] =
        useState<boolean>(false);
    const [
        showCongratulationOnYourChildModal,
        setShowCongratulationOnYourChildModal,
    ] = useState<boolean>(false);

    const queryAtom = useMemo(
        () => fetchNewbornProgramQueryAtom(initialNewbornProgram),
        [initialNewbornProgram],
    );

    const [{ data: newbornProgram }] = useAtom(queryAtom);
    const [{ mutate, isPending }] = useAtom(isBabyBornMutationAtom);

    useEffect(() => {
        if (shouldShowHaveYouGivenBirthModal(newbornProgram)) {
            setShowHaveYouGivenBirthModal(true);
            localStorage.setItem(
                haveYouGivenBirthLastShownKey,
                String(Date.now()),
            );
        }
    }, [newbornProgram]);

    const handleBabyIsBorn = () => {
        mutate(true, {
            onSuccess: () => {
                setShowHaveYouGivenBirthModal(false);
                setShowCongratulationOnYourChildModal(true);
            },
        });
    };

    return {
        newbornProgram,
        isPending,
        showEditDueDateModal,
        showHaveYouGivenBirthModal,
        showCongratulationOnYourChildModal,
        openEditDueDateModal: () => setShowEditDueDateModal(true),
        closeEditDueDateModal: () => setShowEditDueDateModal(false),
        openHaveYouGivenBirthModal: () => setShowHaveYouGivenBirthModal(true),
        closeHaveYouGivenBirthModal: () => setShowHaveYouGivenBirthModal(false),
        closeCongratulationOnYourChildModal: () =>
            setShowCongratulationOnYourChildModal(false),
        handleBabyIsBorn,
    };
};

const shouldShowHaveYouGivenBirthModal = (
    newbornProgram: NewbornProgram,
): boolean => {
    if (newbornProgram.IsBabyBorn || newbornProgram.DaysUntilDueDate > 14) {
        return false;
    }

    const lastShownRaw = localStorage.getItem(haveYouGivenBirthLastShownKey);
    const lastShown = lastShownRaw ? parseInt(lastShownRaw, 10) : undefined;
    const nextAllowedShow = lastShown ? new Date(lastShown) : undefined;
    if (nextAllowedShow) {
        nextAllowedShow.setHours(nextAllowedShow.getHours() + 24);
    }

    return (
        nextAllowedShow === undefined || nextAllowedShow.getTime() <= Date.now()
    );
};
